import type { AllErrorCode } from "./errorCode";
import { getErrorCodeName, isErrorCode } from "./errorCode";
import { igbSendMessage } from "../utils/postMessageHelper";

export type NormalizedError = {
  code: AllErrorCode;
  name: string;
  message: string;
  details?: unknown;
  raw?: unknown;
};

function toNumberOrNull(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (
    typeof value === "string" &&
    value.trim() !== "" &&
    !Number.isNaN(Number(value))
  ) {
    return Number(value);
  }
  return null;
}

function extractErrorCode(error: any): AllErrorCode {
  const candidates: Array<unknown> = [
    error?.code,
    error?.data?.code,
    error?.response?.data?.code,
    error?.error?.code,
  ];

  for (const candidate of candidates) {
    const num = toNumberOrNull(candidate);
    if (num !== null && isErrorCode(num)) return num;
  }

  // 기본값: 알 수 없는 오류
  return 9999 as AllErrorCode;
}

function extractErrorMessage(error: any, fallbackCode: AllErrorCode): string {
  const msg =
    error?.message ||
    error?.data?.message ||
    error?.response?.data?.message ||
    error?.error?.message;

  return typeof msg === "string" && msg.trim() !== ""
    ? msg
    : getErrorCodeName(fallbackCode);
}

export default class ErrorHandler {
  public static parse(error: unknown): NormalizedError {
    const code = extractErrorCode(error as any);
    const message = extractErrorMessage(error as any, code);
    const name = getErrorCodeName(code);

    const details =
      (error as any)?.details ?? (error as any)?.response?.data?.details;

    return {
      code,
      name,
      message,
      details,
      raw: error,
    };
  }

  public static send(
    error: unknown,
    opts?: { onNormalized?: (n: NormalizedError) => void }
  ): void {
    const normalized = this.parse(error);

    // 공통 로깅 포맷
    console.error("[ErrorHandler]", {
      code: normalized.code,
      name: normalized.name,
      message: normalized.message,
      details: normalized.details,
    });

    // 에러 메시지 전송
    igbSendMessage("ERROR", {
      code: normalized.code,
      message: normalized.message,
      details: normalized.details,
      error: (error as any).data,
    });

    // 필요 시 콜백으로 후속 처리 위임
    if (opts?.onNormalized) {
      opts.onNormalized(normalized);
    }
  }
}
